import BlankLayout from '@/layouts/BlankLayout';
import SEO from '@/seo';
import { signIn } from '@holo/auth/react';
import { CustomerType, useCreateUserMutation } from '@holo/gql';
import { Link } from '@holo/link';
import { Button, Circle, H1, H3, Icon, Input, Row, Spinner, Tabs, Text, XStack, YStack } from '@holo/ui';
import { Logo } from '@holo/ui/assets';
import { zodResolver } from '@hookform/resolvers/zod';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import { useRouter } from 'next/router';
import bg from 'public/img/bg-signup.png';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: await serverSideTranslations(locale ?? 'en'),
  };
};

const formBusinessValidationSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(4, { message: 'Must be at least 4 characters' }),
  lastName: z.string().min(1, { message: 'Must be at least 1 characters' }),
  firstName: z.string().min(1, { message: 'Must be at least 1 characters' }),
  company: z.string().min(1, { message: 'Must be at least 1 characters' }),
});

type SignupBusinessFormData = z.infer<typeof formBusinessValidationSchema>;

const tabs = [
  {
    id: 'personal',
    label: 'Personal',
  },
  {
    id: 'business',
    label: 'Business',
  },
];

export type Tab = typeof tabs[0];

type Props = {
  showSpinner: boolean;
  setShowSpinner: (value: boolean) => void;
};

const BusinessForm = ({ showSpinner, setShowSpinner }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [createUser] = useCreateUserMutation();
  const { return_to: returnTo } = router.query;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignupBusinessFormData>({
    resolver: zodResolver(formBusinessValidationSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      company: '',
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setShowSpinner(true);

      const user = await createUser({
        variables: {
          input: {
            type: CustomerType.Wholesale,
            company: data.company,
            firstName: data.firstName,
            lastName: data.lastName,
            password: data.password,
            email: data.email,
            verifyUrl: `${window.location.origin}?t=token`,
          },
        },
      });

      if (!user) {
        setShowSpinner(false);

        toast.error('Something went wrong, please try again', {
          icon: <Icon icon="alertTriangle" size={16} />,
        });

        return;
      }

      const response = await signIn('credentials', {
        username: data.email,
        password: data.password,
        redirect: false,
      });

      if (response?.ok) {
        if (returnTo) {
          router.push(`/${returnTo}`);
        } else {
          router.push('/');
        }
      }
    } catch {
      setShowSpinner(false);

      toast.error('Something went wrong, please try again', {
        icon: <Icon icon="alertTriangle" size={16} />,
      });
    }
  });

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.company')}
          id="company"
          onSubmitEditing={onSubmit}
          name="company"
          placeholder={t('signup.placeholderCompany')}
          control={control}
          borderColor={errors.company ? '$error' : '$border'}
          error={errors.company?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.email')}
          id="email"
          onSubmitEditing={onSubmit}
          name="email"
          placeholder={t('signup.placeholderEmail')}
          autoComplete="email"
          keyboardType="email-address"
          control={control}
          borderColor={errors.email ? '$error' : '$border'}
          error={errors.email?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.firstName')}
          id="firstName"
          onSubmitEditing={onSubmit}
          name="firstName"
          placeholder={t('signup.placeholderFirstName')}
          control={control}
          borderColor={errors.firstName ? '$error' : '$border'}
          error={errors.firstName?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.lastName')}
          id="lastName"
          onSubmitEditing={onSubmit}
          name="lastName"
          placeholder={t('signup.lastName')}
          control={control}
          borderColor={errors.lastName ? '$error' : '$border'}
          error={errors.lastName?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.password')}
          id="password"
          onSubmitEditing={onSubmit}
          name="password"
          placeholder={t('signup.placeholderPassword')}
          type="password"
          control={control}
          borderColor={errors.password ? '$error' : '$border'}
          error={errors.password?.message}
        />
      </Row>

      <Button
        mt="$4"
        mb="$4"
        type="submit"
        id="submit"
        width="100%"
        fontSize="$16"
        color="$background"
        backgroundColor="$contrast"
        hoverStyle={{ backgroundColor: '$contrast' }}
        focusStyle={{ backgroundColor: '$contrast' }}
        disabled={showSpinner}
      >
        {showSpinner ? <Spinner size="small" color="white" /> : t('signup.continue')}
      </Button>
    </form>
  );
};

const formPersonalValidationSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(4, { message: 'Must be at least 4 characters' }),
  lastName: z.string().min(1, { message: 'Must be at least 1 characters' }),
  firstName: z.string().min(1, { message: 'Must be at least 1 characters' }),
});

type SignupPersonalFormData = z.infer<typeof formPersonalValidationSchema>;

const PersonalForm = ({ showSpinner, setShowSpinner }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [createUser] = useCreateUserMutation();
  const { return_to: returnTo } = router.query;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignupPersonalFormData>({
    resolver: zodResolver(formPersonalValidationSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setShowSpinner(true);

      const user = await createUser({
        variables: {
          input: {
            type: CustomerType.Retail,
            firstName: data.firstName,
            lastName: data.lastName,
            password: data.password,
            email: data.email,
            verifyUrl: `${window.location.origin}?t=token`,
          },
        },
      });

      if (!user) {
        setShowSpinner(false);

        toast.error('Something went wrong, please try again', {
          icon: <Icon icon="alertTriangle" size={16} />,
        });

        return;
      }

      const response = await signIn('credentials', {
        username: data.email,
        password: data.password,
        redirect: false,
      });

      if (response?.ok) {
        if (returnTo) {
          router.push(`/${returnTo}`);
        } else {
          router.push('/');
        }
      }
    } catch {
      setShowSpinner(false);

      toast.error('Something went wrong, please try again', {
        icon: <Icon icon="alertTriangle" size={16} />,
      });
    }
  });

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.email')}
          id="email"
          onSubmitEditing={onSubmit}
          name="email"
          placeholder={t('signup.placeholderEmail')}
          autoComplete="email"
          keyboardType="email-address"
          control={control}
          borderColor={errors.email ? '$error' : '$border'}
          error={errors.email?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.firstName')}
          id="firstName"
          onSubmitEditing={onSubmit}
          name="firstName"
          placeholder={t('signup.placeholderFirstName')}
          control={control}
          borderColor={errors.firstName ? '$error' : '$border'}
          error={errors.firstName?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.lastName')}
          id="lastName"
          onSubmitEditing={onSubmit}
          name="lastName"
          placeholder={t('signup.lastName')}
          control={control}
          borderColor={errors.lastName ? '$error' : '$border'}
          error={errors.lastName?.message}
        />
      </Row>

      <Row padding={0} borderBottomWidth={0} marginBottom="$4">
        <Input
          label={t('signup.password')}
          id="password"
          onSubmitEditing={onSubmit}
          name="password"
          placeholder={t('signup.placeholderPassword')}
          type="password"
          control={control}
          borderColor={errors.password ? '$error' : '$border'}
          error={errors.password?.message}
        />
      </Row>

      <Button
        mt="$4"
        mb="$4"
        type="submit"
        id="submit"
        width="100%"
        fontSize="$16"
        color="$background"
        backgroundColor="$contrast"
        hoverStyle={{ backgroundColor: '$contrast' }}
        focusStyle={{ backgroundColor: '$contrast' }}
        disabled={showSpinner}
      >
        {showSpinner ? <Spinner size="small" color="white" /> : t('signup.continue')}
      </Button>
    </form>
  );
};

const Signup = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const [showSpinner, setShowSpinner] = useState(false);

  const activeTab = (router?.query?.type as string) || tabs[0].id;

  const handleTabChange = (tab: Tab) => {
    router.push({
      query: {
        type: tab.id,
      },
    });
  };

  useEffect(() => {
    const hideSpinner = () => setShowSpinner(false);
    router.events.on('routeChangeComplete', hideSpinner);

    return () => {
      router.events.off('routeChangeComplete', hideSpinner);
    };
  }, []);

  return (
    <XStack minHeight="100vh" jc="center" ai="center" backgroundColor="#111">
      <SEO title={t('signup.title')} />

      <YStack
        flex={1}
        width="50%"
        minHeight="100vh"
        justifyContent="center"
        alignItems="center"
        display="none"
        $md={{ display: 'flex' }}
      >
        <YStack maxWidth={430} width="100%" zIndex={1}>
          <Link href="/">
            <Logo w={65} fill="white" />
          </Link>

          <XStack marginBottom="$10" marginTop="$10">
            <Circle backgroundColor="$accentPale" width={24} height={24}>
              <Icon icon="check" size={14} color="#2D6936" />
            </Circle>
            <YStack marginLeft="$2" flexShrink={1}>
              <H3 color="$background">Buy directly from your favorite producers</H3>
              <Text fontSize="$14" color="$subtlePale" lineHeight={24}>
                Our mission is to make it as simple as possible for consumers, business and institutions to order from
                their local producers.
              </Text>
            </YStack>
          </XStack>

          <XStack marginBottom="$10">
            <Circle backgroundColor="$accentPale" width={24} height={24}>
              <Icon icon="check" size={14} color="#2D6936" />
            </Circle>
            <YStack marginLeft="$2" flexShrink={1}>
              <H3 color="$background">Support your local farmers</H3>
              <Text fontSize="$14" color="$subtlePale" lineHeight={24}>
                Our platform makes everything from production to delivery easier for diversified and sustainable
                farmers. 97% of each dollar is paid directly to the producers.
              </Text>
            </YStack>
          </XStack>

          <XStack marginBottom="$10">
            <Circle backgroundColor="$accentPale" width={24} height={24}>
              <Icon icon="check" size={14} color="#2D6936" />
            </Circle>
            <YStack marginLeft="$2" flexShrink={1}>
              <H3 color="$background">Do the right thing for the climate</H3>
              <Text fontSize="$14" color="$subtlePale" lineHeight={24}>
                The best thing you can do to reduce CO2 from the atmosphere is to support farmers that work hard to
                increase their soils health and thus its ability to sequester carbon.
              </Text>
            </YStack>
          </XStack>
        </YStack>
      </YStack>

      <YStack
        flex={1}
        width="50%"
        minHeight="100vh"
        justifyContent="center"
        alignItems="center"
        backgroundColor="$background"
        zIndex={1}
        paddingHorizontal="$5"
      >
        <YStack maxWidth={430} width="100%" justifyContent="center">
          <H1 fontSize="$32" marginBottom="$10">
            Sign up
          </H1>

          <Tabs tabs={tabs} activeTabId={activeTab} onChange={handleTabChange} mb="$8" />

          {activeTab === 'personal' && <PersonalForm showSpinner={showSpinner} setShowSpinner={setShowSpinner} />}
          {activeTab === 'business' && <BusinessForm showSpinner={showSpinner} setShowSpinner={setShowSpinner} />}

          <YStack alignItems="center">
            <Link href="/login">
              <Text>{t('signup.goBack')}</Text>
            </Link>

            <Text fontSize="$12" textAlign="center" marginTop={40} marginBottom={10} lineHeight="$1" color="$darkGray">
              {t('signup.linkPart1')}{' '}
              <Link href="/terms-of-service">
                <Text textDecorationLine="underline" fontSize="$12">
                  {t('signup.termsOfService')}
                </Text>
              </Link>
              , {t('signup.linkPart2')}.
            </Text>
          </YStack>
        </YStack>
      </YStack>
      <Image src={bg} fill alt="background" style={{ position: 'absolute', objectFit: 'cover' }} quality={100} />
    </XStack>
  );
};

Signup.Layout = BlankLayout;

export default Signup;
